// janus-engine/ability/statuses/caster-status-choice.ts — `status_effect_applied_is_caster_choice` (int_0_1, minted 2026-10-07 for Geno's
// Psion v12 PDF — Telepath L6 "Override Emotion": the target becomes Charmed or Frightened, "your choice"). The BROWSER rule; its PHP copy is
// ft_apply_caster_status_choice() (includes/functions/janus/statuses/caster_status_choice.php) — R-151.
//
// RULE: on an ability carrying the flag, every status APPLIER layer that lists 2+ entries is a pick-one. The pick rides use_ability_variable_1
// (the sheet / dock select `input_<id>_user_choice_1_status_effect`); it is honoured only when it names one of those entries (fail closed),
// else — no pick, or a forged one — the FIRST entry applies (the damage-type select's server default). Each multi-entry applier list is then
// cut to the picked entry for the whole dispatch, so every reader (the target's Charmed-resist ADV in skill_roll, contesting_roll_adv_vs_
// status_effect, the apply itself) sees only the pick. Single-entry lists are untouched.
// Self-contained (type-only import) so the PC sheet / action bar bundles can import the option list without pulling the Janus context in.
import type { JanusCtx, Row } from '../context.js';

const record = (v: unknown): Row | null => (v && typeof v === 'object' && !Array.isArray(v) ? v as Row : null);
const upper = (v: unknown): string => String((Array.isArray(v) ? v[0] : v) ?? '').trim().toUpperCase();

export const CASTER_STATUS_CHOICE = 'status_effect_applied_is_caster_choice';
/** The applier layers a pick narrows (the six status_effect_applied_to_{target,caster}_* families). */
export const CHOICE_APPLIER_LAYERS = [
  'status_effect_applied_to_target_after_contesting_roll_failed', 'status_effect_applied_to_target_after_cast', 'status_effect_applied_to_target_on_hit',
  'status_effect_applied_to_caster_after_contesting_roll_failed', 'status_effect_applied_to_caster_after_cast', 'status_effect_applied_to_caster_on_hit',
] as const;

type Readers = { has: (row: Row | null, key: string) => boolean; entries: (row: Row | null, key: string) => unknown[]; value: (row: Row | null, key: string) => unknown };

/** layer_list_entry_uuid(): item_layer_effect, items.item_id, item_id, or a bare string — cut at '|'. */
export function choiceEntryUuid(entry: unknown): string {
  const cut = (value: unknown): string => typeof value === 'string' && value !== '' ? upper(value.split('|')[0]) : '';
  const row = record(entry);
  if (row) return cut(row.item_layer_effect) || cut(record(row.items)?.item_id) || cut(row.item_id);
  return cut(entry);
}

const flagOn = (v: unknown): boolean => { const x = Array.isArray(v) ? v[0] : v; return x != null && x !== '' && x !== 0 && x !== '0' && x !== false; };

/** The pick-one options, in layer order, de-duplicated: every entry of each applier layer that lists 2+ entries. [] = not a choice ability. */
export function casterStatusChoiceIds(read: Readers, ability: Row | null): string[] {
  if (!ability || !flagOn(read.value(ability, CASTER_STATUS_CHOICE))) return [];
  const out: string[] = [];
  for (const layer of CHOICE_APPLIER_LAYERS) {
    const ids = read.entries(ability, layer).map(choiceEntryUuid).filter((id) => id !== '');
    if (ids.length < 2) continue;
    for (const id of ids) if (!out.includes(id)) out.push(id);
  }
  return out;
}

/** A copy of `ability` whose multi-entry applier lists keep only `pick` (both the item_layer_types bucket and a legacy top-level slot). */
export function narrowAbilityToStatusPick(read: Readers, ability: Row, pick: string): Row {
  const buckets = { ...(record(ability.item_layer_types) ?? {}) } as Row;
  const out: Row = { ...ability, item_layer_types: buckets };
  for (const layer of CHOICE_APPLIER_LAYERS) {
    const entries = read.entries(ability, layer);
    if (entries.length < 2) continue;
    const kept = entries.filter((e) => choiceEntryUuid(e) === pick);
    if (kept.length) buckets[layer] = kept; else delete buckets[layer];
    if (Object.prototype.hasOwnProperty.call(out, layer)) {
      if (kept.length) out[layer] = kept; else delete out[layer];
    }
  }
  return out;
}

export interface CasterStatusChoice { pick: string; ability: Row; consumedVariable: boolean }

/** Resolve the dispatch's pick: variable_1 when it names an option, else the first option. null = the ability is not a choice ability. */
export function resolveCasterStatusChoice(ctx: Pick<JanusCtx, 'read'>, ability: Row, variable1: unknown): CasterStatusChoice | null {
  const options = casterStatusChoiceIds(ctx.read, ability);
  if (!options.length) return null;
  const posted = typeof variable1 === 'string' ? upper(variable1.trim()) : '';
  const named = posted !== '' && options.includes(posted);
  const pick = named ? posted : options[0]!;
  // a posted UUID that is not an option is a forged / stale pick — consumed (cleared) too, so it never reaches another reader
  const consumedVariable = named || /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(posted);
  return { pick, ability: narrowAbilityToStatusPick(ctx.read, ability, pick), consumedVariable };
}
